import { supabase } from '@/shared/lib/supabase';
import { logger } from '@/shared/lib/logger';
import { NotFoundError, ValidationError } from '@/shared/lib/errors';
import { Money, centsToWire } from '@shared/money';
import { computeSplit, validatePayers } from '@shared/split';
import type { CurrencyCode } from '@shared/currencies';
import {
  expenseSchema,
  expensePayerSchema,
  expenseParticipantSchema,
  type Expense,
  type ExpenseCreateInput,
  type ExpensePayer,
  type ExpenseParticipant,
} from '@shared/schemas/expenses';

// ==================== Query keys ====================

export const expensesKeys = {
  all: ['expenses'] as const,
  byGroup: (gid: string) => [...expensesKeys.all, 'group', gid] as const,
  byId: (id: string) => [...expensesKeys.all, 'id', id] as const,
  history: (id: string) => [...expensesKeys.all, 'id', id, 'history'] as const,
  comments: (id: string) => [...expensesKeys.all, 'id', id, 'comments'] as const,
};

// ==================== Fetch ====================

export interface ExpenseWithRelations extends Expense {
  payers: ExpensePayer[];
  participants: ExpenseParticipant[];
  category_name: string;
  category_icon: string;
}

/**
 * Un saldo, tal como sale de la vista `group_movements`.
 *
 * No comparte forma con un gasto y no puede: `expenseSchema` exige
 * `category_id` no nulo y `merchant_name_raw` con contenido, y un saldo no
 * tiene ni categoría ni comercio. Intentar validarlo con ese esquema lo tiraría
 * por el `catch` de `toExpenseWithRelations` como «fila corrupta», que es la
 * peor manera de fallar: en silencio y pareciendo que funciona.
 */
export interface SettlementMovement {
  kind: 'settlement';
  id: string;
  group_id: string;
  /** Cuándo se dio por pagado. La lista ordena por esto. */
  occurred_on: string;
  amount_cents: bigint;
  currency: string;
  /** La nota que se escribió al saldar, si se escribió alguna. */
  note: string | null;
  from_participant: { id: string; display_name: string; avatar_url: string | null } | null;
  to_participant: { id: string; display_name: string; avatar_url: string | null } | null;
}

/**
 * Una fila de la lista de un grupo: o un gasto, o un saldo.
 *
 * **Unión discriminada a propósito.** Con un único tipo de «movimiento» con
 * todo opcional, olvidarse de tratar el saldo compilaría y se vería en
 * producción — que es exactamente lo que pasó el 2026-08-28 con
 * `gdpr_consent_at`. Así, leer `merchant_name_raw` sin haber comprobado `kind`
 * no compila.
 */
export type GroupMovement = ({ kind: 'expense' } & ExpenseWithRelations) | SettlementMovement;

// Select compartido para listar gastos con sus relaciones embebidas.
const EXPENSE_LIST_SELECT = `
  *,
  category:categories!inner(name, icon),
  payers:expense_payers(*, participant:participants(display_name, avatar_url)),
  participants:expense_participants(*)
`;

// Convierte una fila cruda de PostgREST al shape con relaciones. Devuelve null
// si la fila no valida (para no tumbar todo el listado por una fila corrupta).
function toExpenseWithRelations(row: unknown): ExpenseWithRelations | null {
  try {
    const parsed = expenseSchema.parse(row);
    const cat = (row as { category: { name: string; icon: string } }).category;
    return {
      ...parsed,
      category_name: cat.name,
      category_icon: cat.icon,
      payers: ((row as { payers: unknown[] }).payers ?? []).map((p) => expensePayerSchema.parse(p)),
      participants: ((row as { participants: unknown[] }).participants ?? []).map((p) =>
        expenseParticipantSchema.parse(p)
      ),
    };
  } catch (e) {
    logger.error('fetchGroupExpenses: fila inválida omitida', {
      id: (row as { id?: string })?.id,
      message: e instanceof Error ? e.message : String(e),
    });
    return null;
  }
}

// Lista los gastos de un grupo. `limit` acota la consulta — la vista de grupo
// solo muestra unos pocos, y traer los miles de gastos de un grupo grande (con
// payers/participants embebidos) en una sola petición la hacía fallar. Sin
// `limit` trae todos (usar solo con paginación por rango en su lugar).
export async function fetchGroupExpenses(
  groupId: string,
  limit?: number,
): Promise<ExpenseWithRelations[]> {
  let query = supabase
    .from('expenses')
    .select(EXPENSE_LIST_SELECT)
    .eq('group_id', groupId)
    .is('deleted_at', null)
    .order('expense_date', { ascending: false })
    .order('created_at', { ascending: false });

  if (typeof limit === 'number') query = query.limit(limit);

  const { data, error } = await query;

  if (error) {
    logger.error('fetchGroupExpenses failed', { code: error.code, groupId });
    throw new Error(error.message);
  }

  return (data ?? [])
    .map(toExpenseWithRelations)
    .filter((e): e is ExpenseWithRelations => e !== null);
}

/**
 * Una fila de `group_movements_page` al tipo que pinta la pantalla.
 *
 * Lo primero que mira es `kind`, y no es una formalidad: la fila de un saldo
 * lleva `category_id` nulo y `merchant_name_raw` vacío, así que pasarla por
 * `expenseSchema` la tiraría por el `catch` como fila corrupta y **el saldo
 * desaparecería de la lista sin que nadie se enterase** — el fallo que este
 * cambio viene a arreglar, reintroducido por la puerta de atrás.
 *
 * El gasto llega con la forma de siempre —la fila entera de `expenses` más la
 * categoría, los pagadores y el reparto— así que se valida tal cual.
 */
function toMovement(row: unknown): GroupMovement | null {
  const r = row as Record<string, unknown>;

  try {
    if (r['kind'] === 'settlement') {
      // Dentro del `try`, y no antes como estaba: `BigInt()` lanza si le llega
      // algo que no es un número, así que un saldo con `amount_cents` raro
      // tumbaba **la página entera** en vez de caer como una fila más. Las dos
      // clases de movimiento se descartan igual y se registran igual.
      return {
        kind: 'settlement',
        id: String(r['id']),
        group_id: String(r['group_id']),
        occurred_on: String(r['occurred_on']),
        amount_cents: BigInt(r['amount_cents'] as string | number),
        currency: String(r['currency']).trim(),
        note: (r['description'] as string | null) ?? null,
        from_participant: r['from_participant'] as SettlementMovement['from_participant'],
        to_participant: r['to_participant'] as SettlementMovement['to_participant'],
      };
    }

    const cat = r['category'] as { name: string; icon: string };
    const parsed = expenseSchema.parse(r);
    return {
      kind: 'expense',
      ...parsed,
      category_name: cat.name,
      category_icon: cat.icon,
      payers: ((r['payers'] as unknown[]) ?? []).map((p) => expensePayerSchema.parse(p)),
      participants: ((r['participants'] as unknown[]) ?? []).map((p) =>
        expenseParticipantSchema.parse(p)
      ),
    };
  } catch (e) {
    // Este `catch` se tragó entero el listado del 2026-08-30: la vista no
    // devolvía `deleted_at`, `expenseSchema` lo exige, y **todos** los gastos
    // se caían por aquí sin dejar rastro. Un `catch` mudo convierte un error de
    // contrato en una pantalla vacía que parece un grupo sin gastos.
    logger.error('toMovement: fila inválida omitida', {
      id: (r['id'] as string | undefined) ?? null,
      kind: (r['kind'] as string | undefined) ?? null,
      message: e instanceof Error ? e.message : String(e),
    });
    return null;
  }
}

/** Por qué columna se ordena la lista de gastos. */
export type ExpensesSortKey = 'date' | 'amount' | 'merchant';

export interface ExpensesPageOptions {
  sort: ExpensesSortKey;
  dir: 'asc' | 'desc';
  /** `null` = todas las categorías. */
  categoryId: string | null;
}

export const ORDEN_POR_DEFECTO: ExpensesPageOptions = {
  sort: 'date',
  dir: 'desc',
  categoryId: null,
};

// Página de movimientos de un grupo: gastos y saldos juntos, ordenados,
// filtrados y contados **en el servidor**. Para la vista "Ver todos" de grupos
// con miles de movimientos.
//
// El orden y el filtro van en la consulta, no sobre las filas ya traídas.
// Ordenar en el navegador sólo reordenaría los 20 de la página actual, con lo
// que «el gasto más caro» sería el más caro de esos veinte: una respuesta falsa
// con toda la apariencia de ser la buena.
//
// Va por RPC y no por PostgREST sobre una tabla o una vista porque el orden es
// de la unión de dos tablas: con una vista, el `Sort` queda por encima de la
// unión y hay que construir los 2011 movimientos —policy por fila incluida—
// para devolver veinte. Medido: 5,3 s. La función lima primero y calcula
// después, y ese mismo grupo baja a 9 ms (incidencia #61).
export async function fetchGroupExpensesPage(
  groupId: string,
  page: number,
  pageSize: number,
  opciones: ExpensesPageOptions = ORDEN_POR_DEFECTO,
): Promise<{ rows: GroupMovement[]; total: number }> {
  const { data, error } = await supabase.rpc('group_movements_page', {
    p_group_id: groupId,
    p_limit: pageSize,
    p_offset: page * pageSize,
    p_sort: opciones.sort,
    p_dir: opciones.dir,
    // Se omite en vez de mandarse a `null`: el generador de tipos de Supabase
    // no sabe decir «opcional y anulable» y tipa el argumento `string`. No
    // mandarlo es lo mismo que mandar NULL —es el valor por defecto de la
    // función— y así no hace falta forzar el tipo.
    ...(opciones.categoryId ? { p_category_id: opciones.categoryId } : {}),
  });

  if (error) {
    logger.error('fetchGroupExpensesPage failed', { code: error.code, groupId });
    throw new Error(error.message);
  }

  const payload = (data ?? { total: 0, rows: [] }) as { total: number; rows: unknown[] };
  const rows = (payload.rows ?? [])
    .map(toMovement)
    .filter((m): m is GroupMovement => m !== null);
  return { rows, total: payload.total ?? rows.length };
}

export async function fetchExpense(expenseId: string): Promise<ExpenseWithRelations> {
  const { data, error } = await supabase
    .from('expenses')
    .select(
      `
      *,
      category:categories!inner(name, icon),
      payers:expense_payers(*, participant:participants(display_name, avatar_url)),
      participants:expense_participants(*)
    `
    )
    .eq('id', expenseId)
    .is('deleted_at', null)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) throw new NotFoundError('Expense', expenseId);

  const parsed = expenseSchema.parse(data);
  const cat = (data as unknown as { category: { name: string; icon: string } }).category;
  return {
    ...parsed,
    category_name: cat.name,
    category_icon: cat.icon,
    payers: ((data as unknown as { payers: unknown[] }).payers ?? []).map((p) =>
      expensePayerSchema.parse(p)
    ),
    participants: ((data as unknown as { participants: unknown[] }).participants ?? []).map((p) =>
      expenseParticipantSchema.parse(p)
    ),
  };
}

// ==================== Create / Update ====================

/**
 * Construye los argumentos del RPC transaccional `expense_write`.
 *
 * El REPARTO se calcula aquí (`computeSplit`, con su residuo determinista) y
 * viaja ya resuelto, para que el algoritmo viva en un único sitio compartido.
 * Todo lo demás —resolver comercio y categoría, tipo de cambio, y las cinco
 * escrituras— lo hace el RPC dentro de UNA transacción.
 *
 * Antes esto eran 8 pasos sueltos desde el cliente: si fallaba uno intermedio
 * quedaba un gasto sin pagadores o sin participantes, incoherente y contando en
 * balances. Además `updateExpense` resolvía comercio/categoría con su propia
 * lógica inline, saltándose el matching difuso y la cuarentena de import.
 */
function buildExpenseWriteArgs(
  expenseId: string,
  input: ExpenseCreateInput,
  isUpdate: boolean,
) {
  const amountCents = BigInt(input.amount_cents.toString());
  const total = Money.fromCents(amountCents, input.currency as CurrencyCode);

  const payersNormalized = input.payers.map((p) => ({
    participant_id: p.participant_id,
    amount_paid_cents: BigInt(p.amount_paid_cents.toString()),
  }));
  const payerValidation = validatePayers(payersNormalized, total);
  if (!payerValidation.valid) {
    throw new ValidationError(payerValidation.message ?? 'Pagadores inválidos');
  }

  const shares = computeSplit({
    expense_id: expenseId,
    total,
    mode: input.split_mode,
    participants: input.participants.map((p) =>
      p.share_value !== null && p.share_value !== undefined
        ? { participant_id: p.participant_id, share_value: p.share_value }
        : { participant_id: p.participant_id }
    ),
    payers: payersNormalized,
  });

  const overrideIn = input.amount_group_currency_override_cents;
  const hasOverride = overrideIn !== null && overrideIn !== undefined;

  // Los parámetros opcionales se OMITEN cuando no hay valor: su DEFAULT en el
  // RPC ya es NULL, y así el objeto encaja con los tipos generados del esquema.
  return {
    p_expense_id: expenseId,
    p_is_update: isUpdate,
    p_group_id: input.group_id,
    p_merchant_name: input.merchant_name.trim(),
    p_amount_cents: centsToWire(amountCents),
    p_currency: input.currency,
    p_split_mode: input.split_mode,
    p_expense_date: input.expense_date,
    p_tags: input.tags ?? [],
    p_is_import: input.is_import ?? false,
    p_payers: payersNormalized.map((p) => ({
      participant_id: p.participant_id,
      amount_paid_cents: centsToWire(p.amount_paid_cents),
    })),
    p_participants: shares.map((s) => ({
      participant_id: s.participant_id,
      share_value: s.share_value,
      amount_owed_cents: centsToWire(s.amount_owed_cents),
    })),
    ...(input.merchant_id ? { p_merchant_id: input.merchant_id } : {}),
    ...(input.category_id ? { p_category_id: input.category_id } : {}),
    ...(input.category_name ? { p_category_name: input.category_name } : {}),
    ...(input.category_icon ? { p_category_icon: input.category_icon } : {}),
    ...(input.description ? { p_description: input.description } : {}),
    ...(hasOverride
      ? {
          p_override_cents: centsToWire(BigInt(overrideIn.toString())),
          ...(input.amount_group_currency_override_ccy
            ? { p_override_ccy: input.amount_group_currency_override_ccy }
            : {}),
        }
      : {}),
  };
}

/**
 * Crea un gasto con sus pagadores y participantes, de forma atómica.
 *
 * El `id` se genera en cliente porque entra en el hash que elige el destinatario
 * del residuo de redondeo (`chooseResidueTarget`) y porque hace la creación
 * idempotente: reintentar una petición que ya se persistió no duplica el gasto.
 */
export async function createExpense(input: ExpenseCreateInput): Promise<Expense> {
  const expenseId = crypto.randomUUID();
  const { data, error } = await supabase.rpc(
    'expense_write',
    buildExpenseWriteArgs(expenseId, input, false),
  );
  if (error) {
    logger.error('createExpense failed', { code: error.code, message: error.message });
    throw new Error(error.message);
  }
  return expenseSchema.parse(data);
}

/**
 * Actualiza un gasto existente. Reemplaza pagadores y participantes completos
 * dentro de la misma transacción. El historial lo registra el trigger
 * `log_expense_change`; `created_by` no cambia.
 */
export async function updateExpense(
  expenseId: string,
  input: ExpenseCreateInput,
): Promise<Expense> {
  const { data, error } = await supabase.rpc(
    'expense_write',
    buildExpenseWriteArgs(expenseId, input, true),
  );
  if (error) {
    logger.error('updateExpense failed', { code: error.code, message: error.message });
    throw new Error(error.message);
  }
  return expenseSchema.parse(data);
}


// ==================== Delete (soft) ====================

export async function softDeleteExpense(expenseId: string): Promise<void> {
  const { error } = await supabase
    .from('expenses')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', expenseId);
  if (error) throw new Error(error.message);
}

// ==================== Category suggestion by merchant ====================

/**
 * Sprint F.1: llama la RPC `suggest_category_for_merchant` para obtener la
 * categoría más usada para un merchant en un grupo. Devuelve NULL si no hay
 * history o si el RPC falla.
 */
export async function suggestCategoryForMerchant(
  groupId: string,
  merchantId: string | null,
  merchantNameRaw: string | null,
): Promise<string | null> {
  const { data, error } = await supabase.rpc('suggest_category_for_merchant', {
    p_group_id: groupId,
    ...(merchantId ? { p_merchant_id: merchantId } : {}),
    ...(merchantNameRaw ? { p_merchant_name_raw: merchantNameRaw } : {}),
  });
  if (error) {
    logger.error('suggest_category_for_merchant failed', { code: error.code });
    return null;
  }
  return (data as string | null) ?? null;
}

// ==================== Suggested workflow ====================

/**
 * Aceptar una sugerencia: pasa `is_suggested` a false → entra en balances.
 * El expense YA existe, así que esto es sólo un UPDATE.
 */
export async function acceptSuggestedExpense(expenseId: string): Promise<void> {
  const { error } = await supabase
    .from('expenses')
    .update({ is_suggested: false })
    .eq('id', expenseId);
  if (error) throw new Error(error.message);
}

/**
 * Descartar una sugerencia: soft-delete, no cuenta en nada.
 * Equivalente funcional a `softDeleteExpense` pero con nombre semántico.
 */
export const dismissSuggestedExpense = softDeleteExpense;

// ==================== Bulk operations (Sprint J) ====================

/**
 * Bulk soft-delete: marca `deleted_at = now()` en múltiples expenses de una.
 * RLS del delete individual (ver 20260713... expenses) aplica per-row —
 * si el user no tiene permisos sobre alguno, ese row NO se actualiza pero
 * los demás sí. Devuelve el count real actualizado.
 */
export async function bulkSoftDeleteExpenses(expenseIds: string[]): Promise<number> {
  if (expenseIds.length === 0) return 0;
  const { data, error } = await supabase
    .from('expenses')
    .update({ deleted_at: new Date().toISOString() })
    .in('id', expenseIds)
    .is('deleted_at', null)
    .select('id');
  if (error) {
    logger.error('bulkSoftDeleteExpenses failed', { code: error.code });
    throw new Error(error.message);
  }
  return (data ?? []).length;
}

/**
 * Bulk change category: aplica el mismo `category_id` a N expenses.
 * Útil tras un import CSV donde todos tienen `category_id = null`.
 */
export async function bulkSetExpensesCategory(
  expenseIds: string[],
  categoryId: string,
): Promise<number> {
  if (expenseIds.length === 0) return 0;
  const { data, error } = await supabase
    .from('expenses')
    .update({ category_id: categoryId })
    .in('id', expenseIds)
    .is('deleted_at', null)
    .select('id');
  if (error) {
    logger.error('bulkSetExpensesCategory failed', { code: error.code });
    throw new Error(error.message);
  }
  return (data ?? []).length;
}
